/**
 * What a board house can actually make.
 *
 * Net Scan answers "is this circuit right" — shorts, unrouted nets, clearance
 * between different nets. This answers the other question, which is "can this be
 * built": is the drill small enough to be a reject, is there enough copper left
 * round the hole after it is drilled, is there an edge to cut the board out of.
 *
 * The two are separate because they fail separately. A board can be
 * electrically perfect and unmanufacturable, and the checks that catch that are
 * about the *process* rather than about the circuit — so the numbers come from
 * whoever is making it, not from the design.
 *
 * Every length is in inches, as everything in the model is. Fab houses publish
 * their capabilities in mils and millimetres, so the profiles below carry the
 * published figure in a comment beside each number rather than leaving a
 * conversion to be trusted.
 */

/** One rule, and how seriously to take it being broken. */
export type Severity = 'error' | 'warning';

export interface DfmRules {
  /** Narrowest copper line the process will hold. */
  minTrackWidth: number;
  /** Copper to copper, between different nets. */
  minClearance: number;
  /** Smallest finished hole. */
  minDrill: number;
  /** Largest hole that is still drilled rather than routed. */
  maxDrill: number;
  /** Copper left round a hole, per side. */
  minAnnularRing: number;
  /** Copper to the board edge. */
  minEdgeClearance: number;
  /** Wall between two holes, edge to edge. */
  minHoleToHole: number;
  /** Narrowest silkscreen line that will still print. */
  minSilkWidth: number;
  /** Smallest silkscreen lettering that will still read. */
  minSilkHeight: number;
}

/**
 * A named set, as a board house publishes it.
 *
 * Kept as a table rather than only as defaults because "will JLC make this"
 * is a different question from "will my usual shop make this", and answering
 * it should not mean typing nine numbers from a web page.
 */
export interface DfmProfile {
  id: string;
  name: string;
  /** Where the numbers came from, so they can be checked and updated. */
  note: string;
  rules: DfmRules;
}

const MIL = 0.001;
const MM = 1 / 25.4;

/**
 * The default: an ordinary two-layer prototype run.
 *
 * Deliberately the loosest of the three, because a rule set that is stricter
 * than the shop you actually use turns the report into a list of things that
 * are fine — and a report full of false alarms is one nobody reads twice.
 */
export const DEFAULT_DFM: DfmRules = {
  minTrackWidth: 6 * MIL,
  minClearance: 6 * MIL,
  minDrill: 0.3 * MM,
  maxDrill: 6.3 * MM,
  minAnnularRing: 5 * MIL,
  minEdgeClearance: 10 * MIL,
  minHoleToHole: 10 * MIL,
  minSilkWidth: 6 * MIL,
  // 24 mil, not the 32 (0.8 mm) a shop publishes as its comfortable figure.
  // Nearly every board here letters its silkscreen at 30 mil, and a default set
  // two thousandths above the value everybody uses reported 2507 near-misses
  // and buried the forty real ones. The published figure belongs in a stricter
  // profile; the loose one is for finding lettering that genuinely will not
  // print.
  minSilkHeight: 24 * MIL,
};

export const DFM_PROFILES: DfmProfile[] = [
  {
    id: 'standard',
    name: 'Standard 2-layer prototype',
    note: 'The loose end of what any shop will take. A good first pass.',
    rules: DEFAULT_DFM,
  },
  {
    id: 'fine',
    name: 'Fine-pitch 4-layer',
    note: 'What a shop charges extra for: 4 mil track and space, 0.2 mm drill. '
      + 'Stricter about silkscreen than the standard set, not looser — a board '
      + 'worth this much care is worth lettering that certainly prints.',
    rules: {
      minTrackWidth: 4 * MIL,
      minClearance: 4 * MIL,
      minDrill: 0.2 * MM,
      maxDrill: 6.3 * MM,
      minAnnularRing: 4 * MIL,
      minEdgeClearance: 8 * MIL,
      minHoleToHole: 8 * MIL,
      minSilkWidth: 5 * MIL,
      // The figure a shop publishes for comfortable, legible silkscreen.
      minSilkHeight: 32 * MIL,
    },
  },
  {
    id: 'home',
    name: 'Home etched / milled',
    note: 'Toner transfer or an isolation mill. Coarse, and forgiving of nothing.',
    rules: {
      minTrackWidth: 12 * MIL,
      minClearance: 12 * MIL,
      minDrill: 0.6 * MM,
      maxDrill: 6.3 * MM,
      minAnnularRing: 10 * MIL,
      minEdgeClearance: 20 * MIL,
      minHoleToHole: 20 * MIL,
      minSilkWidth: 10 * MIL,
      minSilkHeight: 50 * MIL,
    },
  },
];

/**
 * Sets the user has saved, alongside the ones that ship.
 *
 * A shop's capabilities belong to whoever is using the program rather than to
 * any one design — you send every board to the same two or three places — so a
 * saved set follows the user and is offered on every board they open.
 *
 * The id is prefixed so a saved set called "Standard 2-layer prototype" cannot
 * shadow the built-in of that name, and so telling the two apart is a string
 * test rather than a search of both lists.
 */
const CUSTOM = 'custom:';

/** Was this set saved by the user, rather than shipped? */
export function isCustom(id: string): boolean {
  return id.startsWith(CUSTOM);
}

/** Turn a name and nine figures into a set that can be saved. */
export function customProfile(name: string, rules: DfmRules): DfmProfile {
  const trimmed = name.trim() || 'Untitled';
  return {
    id: CUSTOM + trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    name: trimmed,
    note: 'Saved on this machine.',
    rules: { ...rules },
  };
}

/** Every set on offer: the ones that ship, then the ones saved. */
export function allProfiles(saved: readonly DfmProfile[] = []): DfmProfile[] {
  return [...DFM_PROFILES, ...saved];
}

/** A set by id, from the built-ins and whatever has been saved. */
export function profile(
  id: string | undefined, saved: readonly DfmProfile[] = [],
): DfmProfile {
  return allProfiles(saved).find((p) => p.id === id) ?? DFM_PROFILES[0]!;
}

/** Whether two rule sets are the same figures throughout. */
export function sameRules(a: DfmRules, b: DfmRules): boolean {
  return (Object.keys(a) as (keyof DfmRules)[]).every((k) => a[k] === b[k]);
}

/** Every rule this build knows, in the order a report should list them. */
export const RULE_IDS = [
  'outline',
  'track-width',
  'clearance',
  'drill-min',
  'drill-max',
  'annular-ring',
  'hole-to-hole',
  'edge-clearance',
  'silk-width',
  'silk-height',
] as const;

export type RuleId = (typeof RULE_IDS)[number];

/** What each rule is called, and what it is for, in the report. */
export const RULE_LABELS: Record<RuleId, { title: string; why: string }> = {
  outline: {
    title: 'Board outline',
    why: 'Without an edge there is nothing to cut the board out of, and a quote cannot be given.',
  },
  'track-width': {
    title: 'Track width',
    why: 'A track thinner than the process holds may etch away to nothing.',
  },
  clearance: {
    title: 'Copper clearance',
    why: 'Two nets closer than the process resolves may be bridged by the etch.',
  },
  'drill-min': {
    title: 'Drill too small',
    why: 'Below the smallest bit the shop stocks, the hole cannot be made.',
  },
  'drill-max': {
    title: 'Drill too large',
    why: 'Beyond the largest bit, the opening has to be routed instead and priced differently.',
  },
  'annular-ring': {
    title: 'Annular ring',
    why: 'Too little copper round the hole and a drill slightly off centre breaks the connection.',
  },
  'hole-to-hole': {
    title: 'Hole to hole',
    why: 'Two holes too close leave a wall too thin to survive drilling.',
  },
  'edge-clearance': {
    title: 'Copper to edge',
    why: 'Copper close to the cut may be exposed by the router and can short against a case.',
  },
  'silk-width': {
    title: 'Silkscreen width',
    why: 'A line thinner than the screen resolves prints broken or not at all.',
  },
  'silk-height': {
    title: 'Silkscreen height',
    why: 'Lettering below the screen resolution prints as a smudge.',
  },
};
